import { ActionIcon, Tooltip } from '@mantine/core';
import { Icon, Select } from '@uag/react-core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { getNeighboringPoiIndex } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { isInputElement } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { ShortcutAction } from 'app/modules/wallforming/components/scenes/toolbar/types/shortcutAction';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import 'app/modules/wallforming/custom-styles/customSelectStyle.css';

const poiFilterTypes = new Set(Object.values(POIFilterType));
const isPoiFilterType = (value: string): value is POIFilterType => poiFilterTypes.has(value as POIFilterType);
const tooltipProps = { zIndex: 2001 };

export const PoiTypeStepper = () => {
    const { t } = useTranslation();
    const previousPoiIndexRef = useRef<number | undefined>(usePlacementStore.getState().poiSelection.current.index);

    const { selectedPoiType, selectedPoiIndex, selectedPoiSource, pois, setPoiSelection } = usePlacementStore(
        useShallow((state) => ({
            selectedPoiType: state.poiSelection.current.filterType,
            selectedPoiIndex: state.poiSelection.current.index,
            selectedPoiSource: state.poiSelection.current.source,
            pois: state.pois,
            setPoiSelection: state.setPoiSelection
        }))
    );

    const { isCameraAlignDisabled, setDisableCameraAlign } = usePlacementStore(
        useShallow((state) => ({
            isCameraAlignDisabled: state.disableCameraAlignBySource[PoiNavigationSource.PoiStepper],
            setDisableCameraAlign: state.setDisableCameraAlign
        }))
    );

    const poiIndexes = useMemo(
        () =>
            pois.reduce<number[]>((result, poi, index) => {
                if (poi.filterType === selectedPoiType) {
                    result.push(index);
                }
                return result;
            }, []),
        [pois, selectedPoiType]
    );

    const activeIndex = useMemo(
        () => (selectedPoiIndex !== undefined ? Math.max(0, poiIndexes.indexOf(selectedPoiIndex)) : undefined),
        [poiIndexes, selectedPoiIndex]
    );

    const availablePoiTypes = useMemo(
        () => Object.values(POIFilterType).filter((poiType) => pois.some((poi) => poi.filterType === poiType)),
        [pois]
    );

    const poiTypeOptions = useMemo(
        () => availablePoiTypes.map((poiType) => ({ value: poiType, label: poiType })),
        [availablePoiTypes]
    );

    const handleOnNavigation = useCallback(
        (delta: number) => {
            if (activeIndex !== undefined) {
                const nextIndex = (activeIndex + delta + poiIndexes.length) % poiIndexes.length;
                setPoiSelection(selectedPoiType, poiIndexes[nextIndex], PoiNavigationSource.PoiStepper);
            }
        },
        [activeIndex, poiIndexes, selectedPoiType, setPoiSelection]
    );

    const handlePoiTypeChange = useCallback(
        (poiType: POIFilterType, isShiftPressed: boolean) => {
            const neighboringPoiIndex = getNeighboringPoiIndex(
                pois,
                selectedPoiIndex,
                poiType,
                previousPoiIndexRef.current,
                isShiftPressed
            );
            if (neighboringPoiIndex >= 0) {
                previousPoiIndexRef.current = selectedPoiIndex;
                setPoiSelection(poiType, neighboringPoiIndex, PoiNavigationSource.PoiStepper);
            }
        },
        [pois, selectedPoiIndex, setPoiSelection]
    );

    useEffect(() => {
        if (selectedPoiSource !== PoiNavigationSource.PoiStepper) {
            previousPoiIndexRef.current = undefined;
        }
    }, [selectedPoiSource]);

    const handlePoiTypeSelect = useCallback(
        (value: string | null) => {
            if (value && isPoiFilterType(value)) {
                handlePoiTypeChange(value, false);
            }
        },
        [handlePoiTypeChange]
    );

    const handleAlignCameraToggle = useCallback(() => {
        setDisableCameraAlign(PoiNavigationSource.PoiStepper, !isCameraAlignDisabled);
    }, [isCameraAlignDisabled, setDisableCameraAlign]);

    const handleOnKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (isInputElement(event.target) || selectedPoiType === undefined || availablePoiTypes.length < 2) {
                return;
            }

            // Handle poi type switch
            if (event.code === 'Tab') {
                event.preventDefault();
                event.stopPropagation();

                const currentTypeIndex = availablePoiTypes.indexOf(selectedPoiType);
                const direction = event.shiftKey ? -1 : 1;
                const nextType =
                    availablePoiTypes[
                        (currentTypeIndex + direction + availablePoiTypes.length) % availablePoiTypes.length
                    ];
                handlePoiTypeChange(nextType, event.shiftKey);
            }

            // Handle camera alignment toggle
            if (event.code === 'KeyC' && !event.ctrlKey) {
                event.preventDefault();
                event.stopPropagation();
                handleAlignCameraToggle();
            }
        },
        [availablePoiTypes, selectedPoiType, handlePoiTypeChange, handleAlignCameraToggle]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleOnKeyDown, true);
        return () => window.removeEventListener('keydown', handleOnKeyDown, true);
    }, [handleOnKeyDown]);

    if (selectedPoiType === undefined || activeIndex === undefined || poiIndexes.length === 0) {
        return null;
    }

    return (
        <div
            className="pointer-events-auto absolute bottom-15 left-1/2 min-w-52 flex -translate-x-1/2 flex-col rounded-sm border border-border-ultra-light bg-background-default px-2 py-1 shadow-md"
            style={{ zIndex: 1000 }}
            tabIndex={-1}
        >
            <div className="mb-1 flex w-full items-center gap-2 text-sm font-medium text-text-default">
                <div className="shrink-0 whitespace-nowrap font-semibold">
                    <span>{t('labelPoiNavigation')}: </span>
                </div>

                <Tooltip label={`Shortcut: ${ShortcutAction.SwitchPoiType}`} position="top" withArrow {...tooltipProps}>
                    <div className="min-w-0 flex-1" onMouseDown={(e) => e.stopPropagation()}>
                        <Select
                            className="custom-select nav-select"
                            comboboxProps={{ zIndex: 1001 }}
                            data={poiTypeOptions}
                            value={selectedPoiType}
                            onChange={handlePoiTypeSelect}
                        />
                    </div>
                </Tooltip>

                <Tooltip
                    label={isCameraAlignDisabled ? t('labelEnableCameraAlignment') : t('labelDisableCameraAlignment')}
                    position="top"
                    withArrow
                    {...tooltipProps}
                >
                    <ActionIcon
                        aria-label={
                            isCameraAlignDisabled ? t('labelEnableCameraAlignment') : t('labelDisableCameraAlignment')
                        }
                        className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm bg-background-gray-ultra-light text-text-gray-dark hover:bg-background-gray!"
                        onPointerDown={(e) => {
                            handleAlignCameraToggle();
                            e.preventDefault();
                        }}
                    >
                        <Icon icon={isCameraAlignDisabled ? 'videocam_off' : 'videocam'} size="sm" />
                    </ActionIcon>
                </Tooltip>
            </div>

            <div className="flex w-full items-center justify-center gap-2">
                {/* Previous Button */}
                <button
                    className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm text-text-gray-dark hover:bg-background-gray-ultra-light"
                    style={{ zIndex: 2000 }}
                    onPointerDown={(e) => {
                        handleOnNavigation(-1);
                        e.preventDefault();
                    }}
                >
                    <Icon icon="keyboard_arrow_left" size="lg" />
                </button>

                <span className="min-w-12 text-center text-xs font-medium text-text-gray-dark">
                    {activeIndex + 1} / {poiIndexes.length}
                </span>

                {/* Next Button */}
                <button
                    className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm text-text-gray-dark hover:bg-background-gray-ultra-light"
                    onPointerDown={(e) => {
                        handleOnNavigation(1);
                        e.preventDefault();
                    }}
                >
                    <Icon icon="keyboard_arrow_right" size="lg" />
                </button>
            </div>
        </div>
    );
};
